import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StackDrawer from "@/components/ui/stack-drawer";
import { stripeSupportedCountries } from "@/data/countries";
import { ClientsKeys, updateClient } from "@/features/clients/api/clients";
import { Client } from "@/features/clients/types/clients";
import { track } from "@/utils/mixpanel";

interface EditClientDialogProps {
  client: Client;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (client: any) => void;
}

const EditClientDialog = ({
  client,
  isOpen,
  onClose,
  onUpdate,
}: EditClientDialogProps) => {
  const [name, setName] = useState(client.name);
  const [email, setEmail] = useState(client.email);
  const [company, setCompany] = useState(client.company);
  const [addressLine1, setAddressLine1] = useState(client.address?.line1 || "");
  const [addressLine2, setAddressLine2] = useState(client.address?.line2 || "");
  const [city, setCity] = useState(client.address?.city || "");
  const [state, setState] = useState(client.address?.state || "");
  const [postalCode, setPostalCode] = useState(
    client.address?.postalCode || ""
  );
  const [country, setCountry] = useState(client.address?.country || "");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setName(client.name);
      setEmail(client.email);
      setCompany(client.company);
      setAddressLine1(client.address?.line1 || "");
      setAddressLine2(client.address?.line2 || "");
      setCity(client.address?.city || "");
      setState(client.address?.state || "");
      setPostalCode(client.address?.postalCode || "");
      setCountry(client.address?.country || "");
      setErrors({});
    }
  }, [client, isOpen]);

  const queryClient = useQueryClient();

  const { mutate: updateClientMutation, isPending } = useMutation({
    mutationFn: (updatedClient: Partial<Client>) =>
      updateClient(updatedClient.id!, updatedClient),
    onSuccess: (data) => {
      toast.success("Client updated successfully");
      track("client_edited", {
        client_id: data.id,
        email: data.email,
        company: data.company,
        country: data.address?.country,
      });
      queryClient.invalidateQueries({ queryKey: [ClientsKeys.Clients] });
      onUpdate(client);
    },
    onError: () => {
      toast.error("Failed to update client");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    if (!company.trim()) newErrors.company = "Company is required";
    if (!addressLine1.trim())
      newErrors.addressLine1 = "Address line 1 is required";
    if (!city.trim()) newErrors.city = "City is required";
    if (!postalCode.trim()) newErrors.postalCode = "Postal code is required";
    if (!country.trim()) newErrors.country = "Country is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const updatedClient: Partial<Client> = {
      id: client.id,
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      address: {
        line1: addressLine1.trim(),
        line2: addressLine2.trim() || undefined,
        city: city.trim(),
        state: state.trim() || undefined,
        postalCode: postalCode.trim(),
        country: country.trim(),
      },
    };

    updateClientMutation(updatedClient);
    onClose();
  };

  const footerContent = (
    <div className="flex gap-2 justify-end">
      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        disabled={isPending}
      >
        Cancel
      </Button>
      <Button type="submit" form="edit-client-form" disabled={isPending}>
        {isPending ? "Saving..." : "Save Changes"}
      </Button>
    </div>
  );

  return (
    <StackDrawer
      open={isOpen}
      onOpenChange={onClose}
      title="Edit Client"
      subtitle="Update the client details below."
      footer={footerContent}
    >
      <form id="edit-client-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="name">
            Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            autoComplete="name"
            className={errors.name ? "border-destructive" : ""}
            disabled={isPending}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="email">
            Email <span className="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="john@example.com"
            autoComplete="email"
            className={errors.email ? "border-destructive" : ""}
            disabled={isPending}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="company">
            Company <span className="text-destructive">*</span>
          </Label>
          <Input
            id="company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Acme Inc."
            autoComplete="organization"
            className={errors.company ? "border-destructive" : ""}
            disabled={isPending}
          />
          {errors.company && (
            <p className="text-sm text-destructive">{errors.company}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="addressLine1">
            Address Line 1 <span className="text-destructive">*</span>
          </Label>
          <Input
            id="addressLine1"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            placeholder="123 Main St"
            autoComplete="address-line1"
            className={errors.addressLine1 ? "border-destructive" : ""}
            disabled={isPending}
          />
          {errors.addressLine1 && (
            <p className="text-sm text-destructive">
              {errors.addressLine1}
            </p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="addressLine2">Address Line 2</Label>
          <Input
            id="addressLine2"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            placeholder="Suite 100"
            autoComplete="address-line2"
            disabled={isPending}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="city">
              City <span className="text-destructive">*</span>
            </Label>
            <Input
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="New York"
              autoComplete="address-level2"
              className={errors.city ? "border-destructive" : ""}
              disabled={isPending}
            />
            {errors.city && (
              <p className="text-sm text-destructive">{errors.city}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="state">State</Label>
            <Input
              id="state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="NY"
              autoComplete="address-level1"
              disabled={isPending}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="postalCode">
              Postal Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="postalCode"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder="10001"
              autoComplete="postal-code"
              className={errors.postalCode ? "border-destructive" : ""}
              disabled={isPending}
            />
            {errors.postalCode && (
              <p className="text-sm text-destructive">
                {errors.postalCode}
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="country">
              Country <span className="text-destructive">*</span>
            </Label>
            <Select
              value={country}
              onValueChange={setCountry}
              disabled={isPending}
            >
              <SelectTrigger
                id="country"
                className={errors.country ? "border-destructive" : ""}
              >
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                {stripeSupportedCountries.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.country && (
              <p className="text-sm text-destructive">{errors.country}</p>
            )}
          </div>
        </div>
      </form>
    </StackDrawer>
  );
};

export default EditClientDialog;
